import { AUTO_LAUNCH_RULES, CORE_PHYSICS } from "../../config/gameplay";
import { rngUnit } from "../../utils/rng";
import type { CoreColor, CoreState, Vector2 } from "../runtime/types";
import type { OrbitForgeSave } from "../save/types";
import type { UpgradeEffects } from "../upgrades/effects";

const coreColors: readonly CoreColor[] = ["cyan", "pink", "lime", "gold"];
const manualLaunchSpawn: Vector2 = { x: 0, y: 0 };

export interface CoreFactoryResult {
  readonly core: CoreState;
  readonly nextRngState: number;
}

export interface CoreLaunchOptions {
  readonly activeOverchargedCores?: number;
}

export function createCoreFromLaunch(
  save: OrbitForgeSave,
  effects: UpgradeEffects,
  options: CoreLaunchOptions = {}
): CoreFactoryResult {
  const spawn = { ...manualLaunchSpawn };
  const colorRoll = rngUnit(save.rngState);
  const overchargeRoll = rngUnit(colorRoll.state);
  const color = chooseCoreColor(colorRoll.value);
  const activeOverchargedCores = options.activeOverchargedCores ?? 0;
  const isOvercharged =
    effects.overchargedChance > 0 && overchargeRoll.value <= effects.overchargedChance && activeOverchargedCores < CORE_PHYSICS.overchargedActiveCap;
  const rewardMultiplier = isOvercharged ? 10 : 1;
  const launchDirection = normalizeOrFallback(spawn, colorRoll.value * Math.PI * 2);
  const tangent = { x: -launchDirection.y, y: launchDirection.x };
  const speedMultiplier = isOvercharged && rewardMultiplier > 10 ? 0.82 : 1;
  const velocity = {
    x: launchDirection.x * CORE_PHYSICS.launchSpeed * speedMultiplier + tangent.x * 86,
    y: launchDirection.y * CORE_PHYSICS.launchSpeed * speedMultiplier + tangent.y * 86 - 90
  };

  return {
    nextRngState: overchargeRoll.state,
    core: {
      id: save.nextEntityId,
      position: spawn,
      velocity,
      spinRadians: colorRoll.value * Math.PI * 2,
      color,
      radius: CORE_PHYSICS.radius,
      countsAsProgress: true,
      countsAgainstLaunchLimit: true,
      isOvercharged,
      rewardMultiplier,
      zoneMultiplier: 1,
      mode: "reactor",
      bounceCount: 0,
      polarityStacks: 0,
      polarityCharge: 0,
      shellImpactCooldownSeconds: 0,
      polarityCooldownSeconds: 0
    }
  };
}

export function createCoreFromAutoLaunch(
  save: OrbitForgeSave,
  effects: UpgradeEffects,
  options: CoreLaunchOptions = {}
): CoreFactoryResult {
  const colorRoll = rngUnit(save.rngState);
  const angleRoll = rngUnit(colorRoll.state);
  const radiusRoll = rngUnit(angleRoll.state);
  const overchargeRoll = rngUnit(radiusRoll.state);
  const angle = angleRoll.value * Math.PI * 2;
  const spawnRadius = Math.sqrt(radiusRoll.value) * AUTO_LAUNCH_RULES.spawnRadius;
  const spawn = {
    x: Math.cos(angle) * spawnRadius,
    y: Math.sin(angle) * spawnRadius
  };
  const color = chooseCoreColor(colorRoll.value);
  const activeOverchargedCores = options.activeOverchargedCores ?? 0;
  const isOvercharged =
    effects.overchargedChance > 0 && overchargeRoll.value <= effects.overchargedChance && activeOverchargedCores < CORE_PHYSICS.overchargedActiveCap;
  const rewardMultiplier = isOvercharged ? 10 : 1;
  const launchDirection = normalizeOrFallback(spawn, angle);
  const tangent = { x: -launchDirection.y, y: launchDirection.x };
  const speedMultiplier = isOvercharged && rewardMultiplier > 10 ? 0.82 : 0.94;
  const velocity = {
    x: launchDirection.x * CORE_PHYSICS.launchSpeed * speedMultiplier + tangent.x * 62,
    y: launchDirection.y * CORE_PHYSICS.launchSpeed * speedMultiplier + tangent.y * 62 - 84
  };

  return {
    nextRngState: overchargeRoll.state,
    core: {
      id: save.nextEntityId,
      position: spawn,
      velocity,
      spinRadians: radiusRoll.value * Math.PI * 2,
      color,
      radius: CORE_PHYSICS.radius,
      countsAsProgress: true,
      countsAgainstLaunchLimit: true,
      isOvercharged,
      rewardMultiplier,
      zoneMultiplier: 1,
      mode: "reactor",
      bounceCount: 0,
      polarityStacks: 0,
      polarityCharge: 0,
      shellImpactCooldownSeconds: 0,
      polarityCooldownSeconds: 0
    }
  };
}

export function createCoreFromCrate(save: OrbitForgeSave, rewardMultiplier: number): CoreFactoryResult {
  const spawn = { ...manualLaunchSpawn };
  const colorRoll = rngUnit(save.rngState);
  const directionRoll = rngUnit(colorRoll.state);
  const color = chooseCoreColor(colorRoll.value);
  const overchargedMultiplier = Math.max(1, rewardMultiplier);
  const isOvercharged = overchargedMultiplier > 1;
  const launchDirection = normalizeOrFallback(spawn, directionRoll.value * Math.PI * 2);
  const tangent = { x: -launchDirection.y, y: launchDirection.x };
  const speedMultiplier = isOvercharged && overchargedMultiplier > 10 ? 0.82 : 0.92;
  const velocity = {
    x: launchDirection.x * CORE_PHYSICS.launchSpeed * speedMultiplier + tangent.x * 74,
    y: launchDirection.y * CORE_PHYSICS.launchSpeed * speedMultiplier + tangent.y * 74 - 78
  };

  return {
    nextRngState: directionRoll.state,
    core: {
      id: save.nextEntityId,
      position: spawn,
      velocity,
      spinRadians: directionRoll.value * Math.PI * 2,
      color,
      radius: CORE_PHYSICS.radius,
      countsAsProgress: false,
      countsAgainstLaunchLimit: false,
      isOvercharged,
      rewardMultiplier: overchargedMultiplier,
      zoneMultiplier: 1,
      mode: "reactor",
      bounceCount: 0,
      polarityStacks: 0,
      polarityCharge: 0,
      shellImpactCooldownSeconds: 0,
      polarityCooldownSeconds: 0
    }
  };
}

export function chooseCoreColor(colorRoll: number): CoreColor {
  return coreColors[Math.floor(colorRoll * coreColors.length) % coreColors.length];
}

function normalizeOrFallback(point: Vector2, fallbackRadians: number): Vector2 {
  const distance = Math.hypot(point.x, point.y);
  if (distance > 1) {
    return { x: point.x / distance, y: point.y / distance };
  }

  return {
    x: Math.cos(fallbackRadians),
    y: Math.sin(fallbackRadians)
  };
}
